import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { 
  Megaphone, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  EyeOff,
  Info,
  AlertCircle,
  CheckCircle2,
  Filter,
  Calendar,
  User,
  RefreshCw
} from 'lucide-react';

interface Announcement {
  _id: string;
  title: string;
  content: string;
  type: 'info' | 'tip' | 'procedure' | 'reminder';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  target_audience: 'all' | 'resellers' | 'masters' | 'admins';
  is_active: boolean;
  expires_at?: string;
  created_by: {
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const Announcements: React.FC = () => {
  const { isAdmin } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info' as 'info' | 'tip' | 'procedure' | 'reminder',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    target_audience: 'all' as 'all' | 'resellers' | 'masters' | 'admins',
    expires_at: ''
  });

  const announcementTypes = [
    { value: 'info', label: 'Informação Geral', icon: Info, color: 'bg-blue-100 text-blue-800' },
    { value: 'tip', label: 'Dica Importante', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
    { value: 'procedure', label: 'Procedimento', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'reminder', label: 'Lembrete', icon: Info, color: 'bg-purple-100 text-purple-800' }
  ];

  const priorities = [
    { value: 'low', label: 'Baixa', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Média', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Urgente', color: 'bg-red-100 text-red-800' }
  ];

  const audiences = [
    { value: 'all', label: 'Todos' },
    { value: 'resellers', label: 'Revendedores' },
    { value: 'masters', label: 'Masters' },
    { value: 'admins', label: 'Administradores' }
  ];

  const fetchAnnouncements = useCallback(async () => {
    if (!isAdmin() || isLoading) return;
    
    // Cache de 30 segundos para evitar chamadas excessivas
    const now = Date.now();
    if (now - lastFetchTime < 30000) {
      return;
    }
    
    setIsLoading(true);
    setLastFetchTime(now);
    try {
      const params = {
        page: pagination.current_page,
        per: pagination.per_page,
        search: debouncedSearchTerm || undefined,
        type: typeFilter || undefined,
        priority: priorityFilter || undefined,
        is_active: statusFilter !== null ? statusFilter : undefined
      };

      const response = await apiService.getAnnouncementsAdmin(params);
      
      // Ajustar para a estrutura real da resposta da API
      setAnnouncements(response.data || []);
      
      // Se não houver paginação na resposta, usar valores padrão
      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: 1,
          per_page: 15,
          total: (response.data || []).length,
          total_pages: 1
        });
      }
      
      setIsInitialized(true);
    } catch (error) {
      console.error('Erro ao buscar avisos:', error);
      setAnnouncements([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        total_pages: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, isLoading, lastFetchTime, pagination.current_page, pagination.per_page, debouncedSearchTerm, typeFilter, priorityFilter, statusFilter]);

  // Carregamento inicial apenas uma vez
  useEffect(() => {
    if (isAdmin() && !isInitialized) {
      fetchAnnouncements();
    }
  }, [isAdmin, isInitialized, fetchAnnouncements]); // Adicionadas dependências necessárias

  // Debounce para filtros (só executa se já foi inicializado)
  useEffect(() => {
    if (isInitialized) {
      setPagination(prev => ({ ...prev, current_page: 1 }));
      fetchAnnouncements();
    }
  }, [debouncedSearchTerm, typeFilter, priorityFilter, statusFilter, isInitialized, fetchAnnouncements]);

  // Atualizar quando mudar página
  useEffect(() => {
    if (isInitialized) {
      setTimeout(() => {
        fetchAnnouncements();
      }, 100);
    }
  }, [pagination.current_page, isInitialized, fetchAnnouncements]); // Adicionado fetchAnnouncements

  const handleSaveAnnouncement = async () => {
    setIsSaving(true);
    try {
      if (editingAnnouncement) {
        await apiService.updateAnnouncement(editingAnnouncement._id, formData);
      } else {
        await apiService.createAnnouncement(formData);
      }
      
      // Limpar cache do dashboard para forçar atualização
      localStorage.removeItem('dashboard_announcements');
      localStorage.removeItem('dashboard_announcements_time');
      setLastFetchTime(0); // Força nova busca
      
      setShowModal(false);
      setEditingAnnouncement(null);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Erro ao salvar aviso:', error);
      alert('Erro ao salvar aviso');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      priority: announcement.priority,
      target_audience: announcement.target_audience,
      expires_at: announcement.expires_at ? new Date(announcement.expires_at).toISOString().slice(0, 16) : ''
    });
    setShowModal(true);
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este aviso?')) {
      try {
        await apiService.deleteAnnouncement(id);
        
        // Limpar cache do dashboard para forçar atualização
        localStorage.removeItem('dashboard_announcements');
        localStorage.removeItem('dashboard_announcements_time');
        setLastFetchTime(0); // Força nova busca
        
        fetchAnnouncements();
      } catch (error) {
        console.error('Erro ao deletar aviso:', error);
        alert('Erro ao deletar aviso');
      }
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await apiService.toggleAnnouncement(id);
      setLastFetchTime(0); // Força nova busca
      fetchAnnouncements();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'info',
      priority: 'medium',
      target_audience: 'all',
      expires_at: ''
    });
  };

  const handleOpenModal = () => {
    setEditingAnnouncement(null);
    resetForm();
    setShowModal(true);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return <Info className="h-4 w-4 text-blue-600" />;
      case 'tip': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'procedure': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'reminder': return <Info className="h-4 w-4 text-purple-600" />;
      default: return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const classes = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return classes[priority as keyof typeof classes] || classes.medium;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800';
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Acesso Negado</h2>
          <p className="text-gray-500">Acesso negado. Apenas administradores podem gerenciar avisos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Avisos Fixos</h1>
          <p className="text-gray-600">Crie e gerencie avisos permanentes exibidos no dashboard</p>
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">
                <strong>Avisos Fixos:</strong> Informações que os revendedores precisam com frequência, 
                exibidas permanentemente no dashboard. Use para dicas, procedimentos e informações importantes.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => {
              // Limpar cache e forçar refresh
              localStorage.removeItem('dashboard_announcements');
              localStorage.removeItem('dashboard_announcements_time');
              setLastFetchTime(0); // Força nova busca
              fetchAnnouncements();
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Atualizar
          </Button>
          <Button onClick={handleOpenModal} className="flex items-center gap-2">
            <Plus size={16} />
            Novo Aviso
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Título ou conteúdo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                {announcementTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prioridade</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas as prioridades</option>
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter === null ? '' : statusFilter.toString()}
                onChange={(e) => setStatusFilter(e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Avisos */}
      <Card>
        <CardHeader>
          <CardTitle>Avisos ({pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Carregando...</span>
            </div>
          ) : announcements.length > 0 ? (
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div
                  key={announcement._id}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getTypeIcon(announcement.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{announcement.title}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(announcement.priority)}`}>
                            {priorities.find(p => p.value === announcement.priority)?.label}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(announcement.is_active)}`}>
                            {announcement.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{announcement.content}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {audiences.find(a => a.value === announcement.target_audience)?.label}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(announcement.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                          {announcement.expires_at && (
                            <span className="flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Expira: {new Date(announcement.expires_at).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        onClick={() => handleToggleStatus(announcement._id)}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        {announcement.is_active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        {announcement.is_active ? 'Desativar' : 'Ativar'}
                      </Button>
                      <Button
                        onClick={() => handleEditAnnouncement(announcement)}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => handleDeleteAnnouncement(announcement._id)}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum aviso encontrado</p>
            </div>
          )}

          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-600">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
                {pagination.total} avisos
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                  disabled={pagination.current_page === 1}
                  variant="outline"
                  size="sm"
                >
                  Anterior
                </Button>
                
                <span className="text-sm text-gray-600">
                  Página {pagination.current_page} de {pagination.total_pages}
                </span>
                
                <Button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                  disabled={pagination.current_page === pagination.total_pages}
                  variant="outline"
                  size="sm"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingAnnouncement ? 'Editar Aviso' : 'Novo Aviso'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título do aviso"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Conteúdo do aviso"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {announcementTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prioridade *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Público-Alvo *</label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {audiences.map(audience => (
                      <option key={audience.value} value={audience.value}>
                        {audience.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expira em</label>
                  <Input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <Button
                onClick={() => setShowModal(false)}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveAnnouncement}
                disabled={!formData.title || !formData.content || isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {isSaving ? 'Salvando...' : editingAnnouncement ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;